/**
 * What the venue's contract addresses are, when the client will not act on them.
 *
 * Every screen that moves money renders this, and the controls beside it are
 * disabled on the same condition — `<WalletProvider>`'s `chainTrust`, which its
 * two send paths enforce. A notice without the disable would be a warning beside
 * a working button, which is how a warning gets clicked past; the disable
 * without the notice would be a dead screen with no explanation.
 *
 * **It names both addresses.** An address the user cannot see is one they cannot
 * check, and for `ADDRESSES_CHANGED` — a venue with no build-time pin whose
 * contracts have moved since this browser last used it — the whole decision is
 * "is this new address the one you meant", which cannot be asked without
 * showing it. That case is the only one with an action: acting on the change is
 * a decision, and a decision made by clicking *Wrap* is not one.
 *
 * `docs/07` §7 owns the wording. Nothing here reaches viem — every on-chain
 * edge is behind `useWallet()`'s own `import()` (invariant 13).
 */

import { Button } from '@/components/primitives'
import { cx } from '@/lib/cx'
import type { ChainTrustVerdict } from '@/domain/chain-trust'
import { useWallet } from './WalletProvider'

export interface ChainTrustNoticeProps {
  className?: string
}

export function ChainTrustNotice({ className }: ChainTrustNoticeProps) {
  const wallet = useWallet()
  return (
    <ChainTrustNoticeView
      verdict={wallet.chainTrust}
      onTrust={wallet.trustCurrentAddresses}
      {...(className ? { className } : {})}
    />
  )
}

export interface ChainTrustNoticeViewProps {
  verdict: ChainTrustVerdict
  /** Accept a changed pair — `ADDRESSES_CHANGED` only. */
  onTrust: () => void
  className?: string
}

/**
 * The notice itself, for a caller that holds the verdict as a prop —
 * `<TradingSetupModal>`, which `<WalletProvider>` renders with every action
 * passed in. One rendering, so the setup modal and `/money` say the same thing
 * and offer the same way out.
 */
export function ChainTrustNoticeView({ verdict, onTrust, className }: ChainTrustNoticeViewProps) {
  if (verdict.trusted) return null
  // `<WrongChainNotice>` says this one, with the button that fixes it. Two
  // boxes about one problem, one of them without the way out, is one too many.
  if (verdict.code === 'WALLET_ON_WRONG_CHAIN') return null

  const changed = verdict.code === 'ADDRESSES_CHANGED'

  return (
    <div
      role="alert"
      data-testid="chain-trust"
      data-code={verdict.code}
      className={cx(
        'flex flex-col gap-2 rounded-sm bg-danger-bg px-3 py-2 text-body-sm text-danger-text',
        className,
      )}
    >
      <p>{verdict.detail}</p>
      {verdict.observed ? (
        <dl className="flex flex-col gap-1 font-mono text-micro">
          <div className="flex flex-wrap gap-2">
            <dt className="text-text-secondary">This venue says</dt>
            <dd className="break-all">{verdict.observed}</dd>
          </div>
          {verdict.expected ? (
            <div className="flex flex-wrap gap-2">
              <dt className="text-text-secondary">{changed ? 'You trusted' : 'Expected'}</dt>
              <dd className="break-all">{verdict.expected}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}
      {changed ? (
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => onTrust()}>
            I recognise this contract
          </Button>
          <span className="text-micro text-text-secondary">
            Only if you have checked it somewhere other than this page.
          </span>
        </div>
      ) : null}
    </div>
  )
}
